<template>
  <v-dialog :value="open" max-width="620" persistent scrollable>
    <v-card>
      <v-card-title class="py-3 px-4 blue-grey darken-1 white--text">
        <v-icon left color="white">account_tree</v-icon>
        Reasignar concepto
        <v-spacer />
        <v-btn icon dark @click="$emit('cancel')">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <div class="text-caption grey--text text--darken-1 mb-1">{{ esTesoreria ? "Línea del movimiento" : "Comprobante" }}</div>
        <div class="font-weight-medium mb-4">
          <span v-if="esTesoreria">Mov. Nº {{ item.nro_mov }} - </span>{{ item.descripcion || "-" }}
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="text-caption grey--text text--darken-1">Imputación actual</div>
            <div class="font-weight-medium">{{ item.concepto_name || "Sin concepto" }}</div>
            <div class="text-caption grey--text">{{ item.cost_center || "Sin centro" }}</div>
          </div>
          <div class="col-md-6">
            <div class="text-caption grey--text text--darken-1">Importe en esta celda</div>
            <div class="font-weight-medium">{{ formatUtils.formatPrice(item.amount) }}</div>
          </div>
        </div>

        <v-divider class="my-4" />

        <v-autocomplete
          v-model="concepto_selected"
          :items="sortedConceptos"
          item-text="name"
          item-value="id"
          label="Concepto destino*"
          prepend-inner-icon="label"
          outlined
          dense
          return-object
          class="mb-2"
        />
        <v-autocomplete
          v-model="cost_center_selected"
          :items="cost_centers"
          item-text="name"
          item-value="id"
          label="Centro de costo destino"
          prepend-inner-icon="place"
          outlined
          dense
          clearable
          return-object
        />

        <!-- El usuario no elige importes: cada reporte mueve el suyo. Sin esta aclaración la
             reasignación parece incompleta cuando el ajuste contable no coincide con la celda. -->
        <v-alert v-if="esTesoreria" dense text type="info" class="mb-0 text-caption">
          Se reimputa la línea completa del movimiento; los importes, cuentas y fechas no cambian. El reporte de
          tesorería se recalcula y contabilidad emite un asiento de ajuste con fecha de hoy.
        </v-alert>
        <v-alert v-else dense text type="info" class="mb-0 text-caption">
          Se reimputa el ítem completo. No se modifica ninguna cifra fiscal (neto, IVA, total ni CAE).
          El reporte de tesorería se recalcula por lo percibido, incluido lo que se cobre a futuro, y
          contabilidad emite un asiento de ajuste con fecha de hoy por el neto. El IVA y las
          retenciones no se mueven.
        </v-alert>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn text @click="$emit('cancel')">Cancelar</v-btn>
        <v-btn color="blue-grey darken-1" dark :disabled="!concepto_selected" :loading="loading" @click="confirmar"> Reasignar </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/*
Reasigna el concepto (y opcionalmente el centro) de un ítem ya facturado o gastado. Corrige la
imputación en el ORIGEN: el reporte de Resultados por Tesorería se recalcula solo y el motor
contable emite el asiento de ajuste. Ver ReasignacionConceptoService en el backend.

El ítem llega desde el detalle de una celda del reporte, que publica item_type ('voucher' |
'spent' | 'tesoreria') e item_id de la línea del comprobante (o del movimiento manual) que
originó el aporte.
*/
import Constant from "@/constants/sections";
import { generalService } from "@/libs/ws/generalService";
import { formatUtils } from "@/libs/utils/formatUtils";

const ENDPOINTS = {
  voucher: Constant.SECTION_VOUCHERS_REASIGNAR_CONCEPTO,
  spent: Constant.SECTION_SPENTS_REASIGNAR_CONCEPTO,
  tesoreria: Constant.SECTION_TESORERIA_MOVEMENTS_REASIGNAR_CONCEPTO,
};

export default {
  name: "DialogReasignarConcepto",
  props: {
    open: { type: Boolean, default: false },
    item: { type: Object, default: () => ({}) },
    conceptos: { type: Array, default: () => [] },
    cost_centers: { type: Array, default: () => [] },
  },
  data: () => ({
    formatUtils: formatUtils,
    concepto_selected: null,
    cost_center_selected: null,
    loading: false,
  }),
  computed: {
    esTesoreria() {
      return this.item.item_type === "tesoreria";
    },
    // Las vistas cargan los conceptos con enabled:"all" para poder mostrar los históricos en la
    // matriz; como destino de una reimputación solo sirven los vigentes.
    sortedConceptos() {
      return this.conceptos.filter((c) => c.enabled !== false).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    },
  },
  watch: {
    open(value) {
      if (!value) return;
      // El destino arranca vacío: precargar el concepto actual invita a confirmar sin cambiar nada.
      this.concepto_selected = null;
      this.cost_center_selected = this.cost_centers.find((c) => c.name === this.item.cost_center) || null;
    },
  },
  methods: {
    confirmar() {
      const path = ENDPOINTS[this.item.item_type] || Constant.SECTION_VOUCHERS_REASIGNAR_CONCEPTO;

      this.loading = true;
      generalService
        .query(
          {
            item: this.item.item_id,
            concepto: this.concepto_selected.id,
            cost_center: this.cost_center_selected ? this.cost_center_selected.id : null,
          },
          path
        )
        .then((response) => {
          if (response.status) {
            this.$toast.success(response.msg, "Ok");
            this.$emit("reasignado");
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => this.$toast.error((e && e.message) || "No se pudo reasignar el concepto.", "Error"))
        .finally(() => (this.loading = false));
    },
  },
};
</script>
